import Capsule from "layout/Capsule";
import Header from "layout/header/Header";
import PlaceHolderItemList from "components/loader/PlaceHolderItemList";
import useEditorDashboard from "./useEditorDashboard";
import moment from "moment";
import { IoCheckmarkCircle } from "react-icons/io5";

// minuti -> "Xh Ym"
function formatMinutes(min) {
    if (min === null || min === undefined) return '-';
    const sign = min < 0 ? '-' : '';
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = Math.round(abs % 60);
    if (h <= 0) return `${sign}${m}m`;
    return m > 0 ? `${sign}${h}h ${m}m` : `${sign}${h}h`;
}

function fmtDate(d) {
    return d ? moment(d).format('DD/MM HH:mm') : '-';
}

// Timeline orizzontale a 4 step: Shooting -> HD pronto -> Editing -> Consegnato
function Timeline({ shooting, hd, assigned, delivered }) {
    const steps = [
        { label: 'Shooting', active: !!shooting },
        { label: 'HD pronto', active: !!hd },
        { label: 'Editing', active: !!assigned },
        { label: 'Consegnato', active: !!delivered },
    ];
    return (
        <div className="d-flex justify-content-between align-items-start mt-2 mb-1 position-relative">
            {steps.map((s, i) => (
                <div key={i} className="d-flex flex-column align-items-center flex-fill position-relative">
                    {i < steps.length - 1 && (
                        <div
                            className={`position-absolute bg-${steps[i + 1].active ? 'success' : 'secondary'}`}
                            style={{ height: 2, top: 7, left: '50%', width: '100%', opacity: steps[i + 1].active ? 1 : 0.3 }}
                        />
                    )}
                    <div
                        className={`rounded-circle bg-${s.active ? 'success' : 'secondary'}`}
                        style={{ width: 16, height: 16, zIndex: 1, opacity: s.active ? 1 : 0.4 }}
                    />
                    <span className="text-11 text-gray mt-1 text-center">{s.label}</span>
                </div>
            ))}
        </div>
    );
}

function StatBlock({ label, value, color = 'primary' }) {
    return (
        <div className="card flex-fill">
            <div className="card-body py-2 text-center">
                <div className={`fw-bold text-${color}`} style={{ fontSize: '1.3rem' }}>{value}</div>
                <div className="text-11 text-gray">{label}</div>
            </div>
        </div>
    );
}

function QueueCard({ item }) {
    const late = item.is_late;
    const dueMin = item.due_in_minutes;
    return (
        <div className="card mb-2" style={{ borderLeft: `4px solid var(--bs-${late ? 'danger' : (dueMin !== null && dueMin < 480 ? 'warning' : 'success')})` }}>
            <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>{item.product || 'Servizio'}</strong>
                        <div className="text-12 text-gray">{item.organization || ''} · {item.category}</div>
                    </div>
                    <span className={`badge bg-${late ? 'danger' : 'warning'}`}>
                        {late ? 'In ritardo' : (dueMin !== null ? `Scade tra ${formatMinutes(dueMin)}` : 'Senza scadenza')}
                    </span>
                </div>

                <Timeline
                    shooting={item.timeline?.shooting_at}
                    hd={item.timeline?.hd_uploaded_at}
                    assigned={item.timeline?.assigned_at}
                    delivered={null}
                />

                <div className={`alert alert-${late ? 'danger' : 'secondary'} py-1 px-2 mt-1 mb-0 text-12`}>
                    HD caricato {fmtDate(item.timeline?.hd_uploaded_at)}
                    {item.in_queue_minutes !== null && <> · in coda da <strong>{formatMinutes(item.in_queue_minutes)}</strong></>}
                </div>
                {item.quantity ? <div className="text-12 text-gray mt-1">Elementi da elaborare: {item.quantity}</div> : null}
                {item.note_in ? <div className="text-12 text-gray">Note fotografo: {item.note_in}</div> : null}
            </div>
        </div>
    );
}

function CompletedCard({ task, slaMinutes }) {
    const shooting = task.order_item?.order?.appointment?.estimated_start_at;
    const hd = task.date_in;
    const assigned = task.assigned_at;
    const delivered = task.date_out;

    // "Dal caricamento HD alla tua consegna"
    const fromHd = hd || assigned;
    const durationMin = (fromHd && delivered) ? moment(delivered).diff(moment(fromHd), 'minutes') : null;
    const within = (durationMin !== null && slaMinutes) ? durationMin <= slaMinutes : null;

    return (
        <div className="card mb-2">
            <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <strong>{task.order_item?.product?.description || 'Servizio'}</strong>
                        <div className="text-12 text-gray">{task.order_item?.order?.organization?.display_name || ''}</div>
                    </div>
                    <span className="text-12 text-success d-flex align-items-center">
                        <IoCheckmarkCircle className="me-1" /> {fmtDate(delivered)}
                    </span>
                </div>

                <Timeline shooting={shooting} hd={hd} assigned={assigned} delivered={delivered} />

                <div className="d-flex justify-content-between align-items-center mt-1">
                    <div>
                        <div className="fw-bold" style={{ fontSize: '1.2rem' }}>{formatMinutes(durationMin)}</div>
                        <div className="text-11 text-gray">Dal caricamento HD alla tua consegna</div>
                    </div>
                    {within !== null && (
                        <span className={`badge bg-${within ? 'success' : 'danger'}`}>
                            {within ? 'Entro soglia' : 'In ritardo'}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function EditorDashboard() {

    const { tab, setTab, dashboard, queue, completed } = useEditorDashboard();
    const slaMinutes = dashboard?.sla_minutes;

    return (
        <>
            <Header show_agency={false} pageTitle="Le mie consegne" />
            <Capsule>
                {/* Header statistiche */}
                <div className="section mt-2">
                    <div className="d-flex gap-2">
                        <StatBlock
                            label="Tempo medio oggi"
                            value={dashboard ? formatMinutes(dashboard.average_time_today_minutes) : '...'}
                        />
                        <StatBlock
                            label="Consegnati oggi"
                            value={dashboard ? (dashboard.completed_today ?? 0) : '...'}
                            color="info"
                        />
                        <StatBlock
                            label="% Entro soglia"
                            value={dashboard ? (dashboard.within_threshold_percentage !== null ? `${dashboard.within_threshold_percentage}%` : '-') : '...'}
                            color={dashboard && dashboard.within_threshold_percentage !== null && dashboard.within_threshold_percentage < 70 ? 'danger' : 'success'}
                        />
                    </div>
                </div>

                {/* Tab switch */}
                <div className="section">
                    <div className="btn-group w-100" role="group">
                        <button
                            className={`btn ${tab === 'queue' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setTab('queue')}
                        >Da editare</button>
                        <button
                            className={`btn ${tab === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setTab('completed')}
                        >Completati</button>
                    </div>
                </div>

                <div className="section">
                    {tab === 'queue' && (
                        <>
                            {!queue && <PlaceHolderItemList />}
                            {queue && queue.queue.length === 0 && (
                                <div className="card"><div className="card-body text-center text-gray">Nessun lavoro in coda 🎉</div></div>
                            )}
                            {queue && queue.queue.map(item => <QueueCard key={item.task_id} item={item} />)}
                        </>
                    )}
                    {tab === 'completed' && (
                        <>
                            {!completed && <PlaceHolderItemList />}
                            {completed && completed.length === 0 && (
                                <div className="card"><div className="card-body text-center text-gray">Nessun lavoro completato.</div></div>
                            )}
                            {completed && completed.map(task => (
                                <CompletedCard key={task.id} task={task} slaMinutes={slaMinutes} />
                            ))}
                        </>
                    )}
                </div>
            </Capsule>
        </>
    );
}
